import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { useTenant } from '@emerald-works-nova/auth'
import { useStyles } from './style'
import { Box, Slide, Typography, useMediaQuery, useTheme } from '@mui/material'
import { LockTwoTone } from '@mui/icons-material'
import { ActionButton } from '../../../action-buttons'
import { useExternalApp, useABTestLogger, useGroup } from '../../../../hooks'
import { contentSlice, b2cSignupSlice } from '../../../../reducers'
import { CrownIcon } from './icons'
import MTLogoBackground from './icons/MTLogoBackground.svg'
import { GooglePayLogo, ApplePayLogo, PayPalLogo } from '../../../logos'
import CardsLogo from '../../../logos/cards.png'
import PaywallCarousel from './carousel'
import PaywallLaurelWreath from './laurelWreath'
import { PrivacyPolicyLink, TandCLink } from '../../../policy-links'
import { fetchPlans } from '../../../../reducers/b2c-signup/slice'
import { formatAmount } from '../../../../pages/b2c-signup/components/utils'
import consts from '../../../../consts'
import { PAYWALL_VARIANT_STORAGE_KEY } from '..'
import { BROWSER } from '../../../../services/cognito/userGroups'

const PaywallVariant = () => {
  const storedVariant = localStorage.getItem(PAYWALL_VARIANT_STORAGE_KEY)
  const { sendABTestLog } = useABTestLogger()
  const { isInGroup } = useGroup()
  const isBrowserUser = isInGroup(BROWSER)

  const dispatch = useDispatch()
  const [paywallOpen, setPaywallOpen] = useState(false)
  const { breakpoints } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('md'))

  const classes = useStyles()
  const { getMtUrl } = useExternalApp()
  const tenant = useTenant()
  const isClubTenant = tenant.pk === consts.DEFAULT_CLUB_PK
  const paywallTestOn = tenant.paywallTestOn || true

  const contentConsumed = useSelector(contentSlice.selectors.selectMeteringUsed)
  const meteringLimit = useSelector(contentSlice.selectors.selectMeteringLimit)

  const plan = useSelector(b2cSignupSlice.selectors.selectPlan)
  const planPrice = formatAmount(plan?.price?.currency, plan?.price?.unitAmount) || null

  const redirectToSignup = () => {
    window.location.href = getMtUrl('join')
  }
  const [exclusiveSignUpClick] = useEvent([{
    eventName: 'exclusiveSignUpClick',
    onSuccess: redirectToSignup,
    onError: redirectToSignup
  }])

  const handleExclusiveSignupClick = () => {
    if (isBrowserUser && paywallTestOn) {
      sendABTestLog({
        experimentName: 'paywall',
        experimentVariant: storedVariant,
        experimentAction: 'signup_click'
      })
      console.log('[PAYWALL] AB Test Signup Click log sent. Variant:', storedVariant)
    }

    exclusiveSignUpClick.trigger({
      contentConsumed,
      meteringLimit
    })
  }

  useEffect(() => {
    if (!paywallOpen) {
      setTimeout(() => setPaywallOpen(true), 3000)
    }
  }, [paywallOpen])

  useEventsOnViewLoad(() => {
    dispatch(fetchPlans())
  }, [dispatch])

  if (!isClubTenant || !planPrice) return null

  const PricingMessage = () => {
    const parseUnit = (duration, unit, type) => {
      if (duration === 1 && unit.endsWith('s')) {
        return type === 'trial' ? `${duration} ${unit.slice(0, -1)}` : unit.slice(0, -1)
      }
      return `${duration} ${unit}`
    }

    const planUnit = plan?.intervalUnit && parseUnit(plan.intervalLength, plan.intervalUnit, 'plan')
    const trialLength = plan?.trialLength
    const trialUnit = plan?.trialUnit && parseUnit(plan.trialLength, plan.trialUnit, 'trial')
    // When Trial is set to off, Trial Length is sent as 0
    const shouldDisplayTrialMessage = plan?.hasTrial === true && trialLength > 0

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {isMobile
          ? (
            <Typography variant='h5' className={classes.ctaPriceMobile}>
              <CrownIcon /> Get <strong>Unlimited Access</strong> <br /> from <strong>{planPrice}</strong> per/{planUnit} {shouldDisplayTrialMessage && ` - ${trialUnit} FREE trial`}
            </Typography>
            )
          : (
            <>
              <Typography className={classes.ctaPrice}>
                <CrownIcon /> From {planPrice} per/{planUnit} {shouldDisplayTrialMessage && ` - ${trialUnit} FREE trial`}
              </Typography>
            </>
            )}

      </Box>
    )
  }

  return (
    <Slide
      direction='up' in={paywallOpen} timeout={1500} easing={{
        enter: 'cubic-bezier(0, 0.55, 0.45, 1)'
      }} mountOnEnter unmountOnExit
    >
      <Box className={classes.banner}>
        <img src={MTLogoBackground} alt='MT Logo Background' className={classes.mtLogoBackground} />
        <Box className={classes.cta}>
          <Typography variant='h4' className={classes.ctaTitle}>
            Unlock our <strong>premium content</strong> by subscribing today
          </Typography>
          <PricingMessage />
          <ActionButton
            className={classes.ctaButton}
            size='l'
            variant='grey'
            label='Try for Free'
            onClick={handleExclusiveSignupClick}
          />
          <Typography className={classes.ctaSignIn} variant='body2'>
            Already a member? <Link className={classes.ctaLink} to='/login'>Sign in now</Link>
          </Typography>
        </Box>
        <Box className={classes.trust}>
          {!isMobile && (
            <Box className={classes.partners}>
              <PaywallLaurelWreath />
              <PaywallCarousel />
            </Box>
          )}
          <Box className={classes.payment}>
            <Box sx={{ minWidth: '180px' }}>
              <Typography fontWeight='bold'>
                Cancel Online Anytime.
              </Typography>
              <Box className={classes.paymentDetails}>
                <LockTwoTone fontSize='0.75rem' />
                <Typography variant='caption' lineHeight='0.75'>
                  Backed by secure global payment systems
                </Typography>
              </Box>
            </Box>
            <Box className={classes.paymentOptions}>
              <Box className={classes.paymentLogo} padding={1.5}>
                <GooglePayLogo />
              </Box>
              <Box className={classes.paymentLogo} padding={1.5}>
                <ApplePayLogo />
              </Box>
              <Box className={classes.paymentLogo} padding={0.5}>
                <PayPalLogo />
              </Box>
              <Box backgroundColor='grey.200' borderRadius={1} height='30px' width='100px'>
                <img src={CardsLogo} alt='Credit cards' />
              </Box>
            </Box>
          </Box>
          <Box className={classes.policies}>
            <Typography>
              <PrivacyPolicyLink style={{ color: 'white', textDecoration: 'underline' }} /> and <TandCLink style={{ color: 'white', textDecoration: 'underline' }}> Terms and Conditions </TandCLink>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Slide>
  )
}

export default PaywallVariant
